import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw, ShoppingBag } from "lucide-react";
import { Eyebrow } from "../components/motion";
import { useCart } from "../context/CartContext";
import { useProduct } from "../hooks/useProducts";
import { formatPrice } from "../config/site";

const QUESTIONS = [
  {
    id: "need",
    title: "Qual è la tua principale esigenza?",
    options: [
      { value: "caduta", label: "Caduta e diradamento", desc: "Trovo molti capelli sulla spazzola o sul cuscino" },
      { value: "grasso", label: "Cuoio capelluto grasso", desc: "Le radici si appesantiscono in fretta" },
      { value: "secco", label: "Cuoio capelluto secco", desc: "Sento la cute tirare, a volte desquamazione" },
      { value: "sensibile", label: "Cuoio capelluto sensibile", desc: "Prurito o rossore con molti prodotti" },
      { value: "fibra", label: "Capelli crespi o stressati", desc: "Al risveglio sono disordinati, con nodi e pieghe" },
    ],
  },
  {
    id: "time",
    title: "Da quanto tempo lo noti?",
    options: [
      { value: "recente", label: "Da poco", desc: "È iniziato nelle ultime settimane" },
      { value: "mesi", label: "Da qualche mese", desc: "Lo noto da un po' e sta peggiorando" },
      { value: "tanto", label: "Da molto tempo", desc: "È una situazione che mi porto dietro da tempo" },
    ],
  },
  {
    id: "commitment",
    title: "Che percorso desideri?",
    options: [
      { value: "soft", label: "Voglio iniziare", desc: "Un primo passo per capire come risponde la mia cute" },
      { value: "full", label: "Un percorso completo", desc: "Sono pronta a prendermi cura dei capelli con costanza" },
    ],
  },
];

function recommend(answers) {
  const need = answers.need;
  if (need === "grasso") return "scrub-te-matcha";
  if (need === "secco" || need === "sensibile") return "scrub-ciliegia";
  if (need === "fibra") return "fibra-di-seta";
  if (answers.commitment === "full") {
    return answers.time === "tanto" ? "kit-full-transformation" : "kit-boost-fill";
  }
  return "kit-start-grow";
}

const Result = ({ slug, onRestart }) => {
  const { product } = useProduct(slug);
  const { add } = useCart();
  if (!product) return null;

  return (
    <motion.div
      data-testid="quiz-result"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="bg-white rounded-[2.5rem] border border-lilac-light overflow-hidden shadow-[0_30px_70px_-30px_rgba(139,92,246,0.4)]"
    >
      <div className="grid sm:grid-cols-2">
        <img src={product.image} alt={product.name} className="w-full h-64 sm:h-full object-cover" />
        <div className="p-8 sm:p-10 flex flex-col justify-center">
          <Eyebrow>Il tuo percorso consigliato</Eyebrow>
          <h2 className="mt-3 font-serif text-2xl sm:text-3xl text-plum tracking-tight">{product.name}</h2>
          <p className="mt-3 text-sm text-plum/60 leading-relaxed">{product.short}</p>
          <p className="mt-5 font-serif text-3xl text-plum">{formatPrice(product.price)}</p>
          <div className="mt-6 flex flex-col gap-2">
            <button
              data-testid="quiz-add-btn"
              onClick={() => add(product)}
              className="flex items-center justify-center gap-2 py-4 rounded-full bg-lilac text-white font-bold text-sm uppercase tracking-[0.12em] hover:bg-lilac-dark active:scale-[0.98] transition-all duration-300"
            >
              <ShoppingBag className="w-4 h-4" /> Aggiungi al carrello
            </button>
            <Link
              to={`/prodotto/${product.slug}`}
              data-testid="quiz-details-link"
              className="text-center py-3 rounded-full border-2 border-plum/15 text-plum font-bold text-xs uppercase tracking-[0.12em] hover:border-lilac-dark hover:text-lilac-dark transition-colors duration-300"
            >
              Scopri di più
            </Link>
            <button
              data-testid="quiz-restart-btn"
              onClick={onRestart}
              className="mt-2 inline-flex items-center justify-center gap-1.5 text-xs text-plum/50 hover:text-plum transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Ricomincia il quiz
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default function QuizPage() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const done = step >= QUESTIONS.length;

  const choose = (qid, value) => {
    setAnswers((a) => ({ ...a, [qid]: value }));
    setStep((s) => s + 1);
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
  };

  const question = QUESTIONS[step];

  return (
    <main data-testid="quiz-page" className="py-14 sm:py-20 min-h-[70vh]">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center">
          <Eyebrow className="justify-center">Percorso guidato</Eyebrow>
          <h1 className="mt-4 font-serif text-3xl sm:text-4xl text-plum tracking-tight">
            Scegli il percorso più adatto a te
          </h1>
          <p className="mt-3 text-sm sm:text-base text-plum/60">
            Tre domande, un minuto, la routine giusta per i tuoi capelli.
          </p>
        </div>

        {!done && (
          <div className="mt-8 flex justify-center gap-2" data-testid="quiz-progress">
            {QUESTIONS.map((q, i) => (
              <span
                key={q.id}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i < step ? "w-10 bg-lilac" : i === step ? "w-14 bg-lilac-dark" : "w-10 bg-lilac-light"
                }`}
              />
            ))}
          </div>
        )}

        <div className="mt-10">
          <AnimatePresence mode="wait">
            {!done ? (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <h2 className="font-serif text-xl sm:text-2xl text-plum text-center">{question.title}</h2>
                <div className="mt-7 space-y-3">
                  {question.options.map((opt) => (
                    <button
                      key={opt.value}
                      data-testid={`quiz-option-${question.id}-${opt.value}`}
                      onClick={() => choose(question.id, opt.value)}
                      className="w-full text-left bg-white border-2 border-lilac-light rounded-2xl p-5 hover:border-lilac-dark hover:bg-lilac-soft/60 hover:-translate-y-0.5 transition-all duration-300 group"
                    >
                      <span className="flex items-center justify-between gap-3">
                        <span>
                          <span className="block font-semibold text-plum text-sm sm:text-base">{opt.label}</span>
                          <span className="block text-xs sm:text-sm text-plum/50 mt-1">{opt.desc}</span>
                        </span>
                        <ArrowRight className="w-4 h-4 text-lilac-dark opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                      </span>
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button
                    data-testid="quiz-back-btn"
                    onClick={() => setStep((s) => s - 1)}
                    className="mt-6 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-plum/50 hover:text-plum transition-colors"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Indietro
                  </button>
                )}
              </motion.div>
            ) : (
              <Result slug={recommend(answers)} onRestart={restart} />
            )}
          </AnimatePresence>
        </div>
      </div>
    </main>
  );
}
